/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  TableComponent,
  SearchComponent,
  AlertComponent,
  IconComponent,
  ButtonComponent,
  ActionEvent,
  DialogService,
  DialogRef,
  ConfirmDialogComponent,
  ResultsCounterComponent,
} from 'nebula-ui-kit';
import { TranslationService } from '@nebula-shared/data-access';

@Component({
  selector: 'app-select-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './select-dialog.component.html',
  styleUrls: ['.//select-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    SearchComponent,
    AlertComponent,
    IconComponent,
    ButtonComponent,
    ResultsCounterComponent,
  ],
})
export class SelectDialogComponent<T = any> implements OnInit, OnDestroy {
  dialogRef = inject(DialogRef, { optional: true });
  readonly dialogService = inject(DialogService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly injector = inject(Injector);
  private readonly translation = inject(TranslationService);

  @Input() title = '';
  @Input() endpoint?: string;
  @Input() columns: any[] = [];
  @Input() searchPlaceholder = '';
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input() defaultPageSize = 10;
  @Input() multiSelect = true;
  @Input() loadDataFn?: (params: any) => Promise<{ data: T[]; total: number }>;
  @Input() selectedItems: T[] = [];
  @Input() itemIdKey = 'id';

  @Input() displayKey = 'nombre';
  @Input() displayFn?: (item: T) => string;
  @Input() pageSize = 10;
  @Input() showPagination = true;
  @Input() enableSearch = true;
  @Input() filters?: any;

  Math = Math;

  readonly rawData = signal<any[]>([]);
  totalItems = signal(0);
  loading = signal(false);
  searchTerm = signal('');
  currentPageSize = signal(10);
  pageIndex = signal(0);
  selectedItemsInternal = signal<any[]>([]);
  selectedIds = signal<any[]>([]);
  showError = signal(false);
  errorTitle = signal('');
  errorMessage = signal('');

  displayData = signal<any[]>([]);

  totalPages = computed(() => Math.ceil(this.totalItems() / this.currentPageSize()));
  hasNextPage = computed(() => this.pageIndex() + 1 < this.totalPages());
  hasPreviousPage = computed(() => this.pageIndex() > 0);

  visiblePages = computed(() => {
    const currentPage = this.pageIndex() + 1;
    const totalPages = this.totalPages();
    const maxVisible = 5;

    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });
  protected t(key: string): string {
    return this.translation.translate(key);
  }

  tableConfig = computed(() => ({
    columns: this.columns,
    showPagination: false,
    paginationLabels: {
      itemsPerPage: this.t('PAGINATION.PER_PAGE'),
      nextPage: this.t('PAGINATION.NEXT'),
      previousPage: this.t('PAGINATION.PREV'),
    },
    paginationInfo: {
      currentPage: this.pageIndex() + 1,
      totalPages: this.totalPages(),
      pageSize: this.currentPageSize(),
      totalItems: this.totalItems(),
    },
  }));

  private currentRequestId = 0;
  private isDestroyed = false;
  private dataLoaded = false;

  ngOnInit() {
    const initialPageSize = this.pageSize || this.defaultPageSize;
    this.currentPageSize.set(initialPageSize);
    this.selectedItemsInternal.set([...this.selectedItems]);

    const ids = this.selectedItemsInternal()
      .map((item: any) => item[this.itemIdKey])
      .filter((id: any) => !!id);
    this.selectedIds.set([...ids]);

    this.loadData();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.currentRequestId++;
  }

  onSearchChange(term: string): void {
    if (this.loading() || this.isDestroyed) return;

    this.searchTerm.set(term);
    this.pageIndex.set(0);
    this.loadData();
  }

  onPageChange(event: any): void {
    if (this.loading() || this.isDestroyed) return;

    let newPageIndex = event.pageIndex;
    let newPageSize = event.pageSize;

    if (typeof event === 'number') {
      newPageIndex = event - 1;
      newPageSize = this.currentPageSize();
    }

    if (event.page) {
      newPageIndex = event.page - 1;
      newPageSize = event.pageSize || this.currentPageSize();
    }

    this.pageIndex.set(newPageIndex);
    this.currentPageSize.set(newPageSize);
    this.loadData();
  }

  goToNextPage(): void {
    if (!this.hasNextPage()) return;
    this.pageIndex.set(this.pageIndex() + 1);
    this.loadData();
  }

  goToPreviousPage(): void {
    if (!this.hasPreviousPage()) return;
    this.pageIndex.set(this.pageIndex() - 1);
    this.loadData();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages()) return;
    this.pageIndex.set(page);
    this.loadData();
  }

  onSelectionChange(selected: T[]): void {
    if (this.isDestroyed) return;

    let newSelection = [...selected];

    if (!this.multiSelect && newSelection.length > 1) {
      newSelection = [newSelection[newSelection.length - 1]];
    }

    this.selectedItemsInternal.set(newSelection);

    const ids = newSelection.map((item: any) => item[this.itemIdKey]).filter((id: any) => !!id);
    this.selectedIds.set([...ids]);

    this.cdr.markForCheck();
  }

  private showTemporaryWarning(message: string): void {
    this.errorTitle.set('Advertencia');
    this.errorMessage.set(message);
    this.showError.set(true);

    setTimeout(() => {
      this.showError.set(false);
      this.errorMessage.set('');
    }, 3000);
  }

  onTableAction(event: ActionEvent<any>): void {
    // to do
  }

  async loadData(): Promise<void> {
    if (this.isDestroyed) return;

    const requestId = ++this.currentRequestId;
    this.loading.set(true);
    this.showError.set(false);

    try {
      const params = {
        search: this.searchTerm(),
        page: this.pageIndex() + 1,
        limit: this.currentPageSize(),
        offset: this.pageIndex() * this.currentPageSize(),
        ...this.filters,
      };

      let result: { data: any[]; total: number };

      if (this.loadDataFn) {
        const loadedData = await this.loadDataFn(params);
        result = {
          data: loadedData.data as any[],
          total: loadedData.total,
        };
      } else if (this.endpoint) {
        result = await this.loadFromEndpoint(this.endpoint, params);
      } else {
        throw new Error('Se requiere endpoint o loadDataFn');
      }

      if (requestId === this.currentRequestId && !this.isDestroyed) {
        this.rawData.set([...result.data]);
        this.displayData.set([...result.data]);
        this.totalItems.set(result.total);
        this.dataLoaded = true;
        this.cdr.markForCheck();
      }
    } catch (error) {
      if (requestId === this.currentRequestId && !this.isDestroyed) {
        this.errorTitle.set('Error al cargar datos');
        this.errorMessage.set(
          error instanceof Error ? error.message : 'Ocurrió un error inesperado',
        );
        this.showError.set(true);
      }
    } finally {
      if (requestId === this.currentRequestId && !this.isDestroyed) {
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    }
  }

  private async loadFromEndpoint(
    endpoint: string,
    params: any,
  ): Promise<{ data: any[]; total: number }> {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key].toString());
      }
    });

    const url = `${endpoint}?${queryParams.toString()}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const result = await response.json();

    return {
      data: result.data || result.items || result,
      total: result.total || result.totalCount || result.data?.length || 0,
    };
  }

  onSave(): void {
    if (this.isDestroyed) return;

    if (!this.multiSelect && this.selectedItemsInternal().length > 1) {
      this.showTemporaryWarning('Solo se puede seleccionar un elemento');
      return;
    }

    this.dialogRef?.close(this.selectedItemsInternal());
  }

  async onCancel(): Promise<void> {
    if (this.isDestroyed) return;

    if (this.selectedItemsInternal().length > 0 && this.dataLoaded) {
      const confirmed = await this.showConfirmDialog();
      if (!confirmed) {
        return;
      }
    }
    this.dialogRef?.close([]);
  }

  private async showConfirmDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
        data: {
          title: this.t('ACTIONS.CONFIRM_DEACTIVATE.TITLE'),
          message: this.t('ACTIONS.CONFIRM_DEACTIVATE.MESSAGE'),
          type: 'warning',
          confirmText: this.t('ACTIONS.CONFIRM_DEACTIVATE.CONFIRM'),
          cancelText: this.t('ACTIONS.CONTINUE'),
        },
        size: 'sm',
      });

      const subscription = dialogRef.afterClosed().subscribe((result: boolean) => {
        resolve(result === true);
        subscription.unsubscribe();
      });
    });
  }
}
